<template>
	<Modal :class="classes"
	    ref="innerModal"
	    :className="prefixCls + '-wrap'"
	    :styles="modalStyles"
	    :value="canOpen && value"
	    :width="width"
	    :footerHide="true"
	    :closable="false"
	    :mask-closable="false"
	    @input="onInput"
	    @on-cancel="onCancel"
	    @on-ok="onOk"
	    @on-hidden="onHidden"
	    @on-visible-change="onVisibleChange"
	    @click.native="maskClick">
		<!-- 顶部标题栏 -->
		<span slot="close"></span>
		<div v-show="title.length > 0 || closable"
		    :class="prefixCls + '-header'"
		    :style="{background: background}">
			{{title}}
			<common-icon v-if="closable"
			    type="ios-close"
			    :class="prefixCls + '-close'"
			    :size="28"
			    @click.native="onClose"></common-icon>
		</div>
		<!-- beforeEnter进度条提示 -->
		<loading-bar ref="loadingBar"
		    style="position: absolute; top: 50px;"></loading-bar>
		<!-- 内容区域（预加载占位） -->
		<div v-show="loading"
		    :class="prefixCls + '-body'"
		    :style="bodyStyles">
		</div>
		<div v-show="!loading"
		    :class="prefixCls + '-body'"
		    :style="bodyStyles">
			<slot></slot>
		</div>
		<!-- 底部按钮栏 -->
		<div v-show="loading"
		    :class="prefixCls + '-footer'"
		    :style="{background: background}"></div>
		<div v-show="!loading && normalBtns.length > 0"
		    :class="prefixCls + '-footer'"
		    :style="{background: background}">
			<Button v-for="(btn, index) in normalBtns"
			    :key="index"
			    :type="btn.type"
			    :loading="loadingBtnName == btn.name"
			    :disabled="btn.disabled || (btnHookRunning && loadingBtnName != btn.name)"
			    @click="onBtnClickHandler(btn)">
				{{btn.text}}
				<count-down v-if="btn.countDown >= 0"
				    :value="btn.countDown"
				    @input="(value) => setCountDown(btn, value)"
				    @count-start="btn.countStart"
				    @count-end="btn.countEnd">
					<template v-slot:default="{formatValue}">
						({{formatValue}})
					</template>
				</count-down>
			</Button>
		</div>
	</Modal>
</template>
<script>
import CommonIcon from '../common-icon'
import Modal from '../modal'
import Icon from '../icon'
import emitter from '../../mixins/emitter'
import { runQueue, type, deepCopy } from '../../utils/assist'
import LoadingBar from './loading-bar'
import CountDown from '../count-down'

function normalizeBtnClickHook(hooks) {
	var result = {}

	if (type(hooks) == "object") {
		Object.keys(hooks).forEach(name => {
			if (type(hooks[name]) == "function") {
				return result[name] = [hooks[name]]
			}

			if (type(hooks[name]) == "array") {
				return result[name] = hooks[name]
			}
		})
	}

	return result
}

export default {
	name: "SmartModal",
	components: {
		CommonIcon,
		Modal,
		Icon,
		LoadingBar,
		CountDown
	},
	mixins: [emitter],
	provide() {
		return {
			$modal: this
		}
	},
	props: {
		value: Boolean,

		width: {
			type: Number,
			default: 960
		},
		height: {
			type: [Number, String],
			default: "auto"
		},
		title: {
			type: String,
			default: ""
		},
		verticalCenter: {
			type: Boolean,
			default: true
		},
		top: {
			type: Number,
			default: 20
		},
		buttons: {
			type: Array,
			default: function() {
				return [
					{ type: "primary", text: "确定", name: "confirm" },
					{ type: "default", text: "取消", name: "cancel" }
				]
			}
		},
		closable: {
			type: Boolean,
			default: true
		},
		maskClosable: {
			type: Boolean,
			default: false
		},
		// 是否异步加载内容
		async: {
			type: Boolean,
			default: true
		},
		// 背景色
		background: {
			type: String,
			default: "#FFFFFF"
		},
		// 窗口打开前
		modalBeforeOpen: {
			type: Function
		},
		// 加载前
		modalBeforeEnter: {
			type: Function
		},
		// 加载后
		modalAfterEnter: {
			type: Function
		},
		// 按钮点击事件
		modalBeforeBtnClick: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			loading: this.async,
			canOpen: false,
			// 按钮相关
			normalBtns: [],
			btnHookRunning: false,
			btnClickHook: normalizeBtnClickHook(this.modalBeforeBtnClick),
			loadingBtnName: "",
			// 弹出框生命周期钩子
			beforeOpenHook: this.modalBeforeOpen ? [this.modalBeforeOpen] : [],
			beforeEnterHook: this.modalBeforeEnter ? [this.modalBeforeEnter] : [],
			afterEnterHook: this.modalAfterEnter ? [this.modalAfterEnter] : [],
		}
	},
	computed: {
		prefixCls() {
			return "ivu-smart-modal"
		},
		classes() {
			return {
				[this.prefixCls]: true,
				[this.prefixCls + '-vertical-center']: this.verticalCenter
			}
		},
		modalStyles() {
			return {
				"top": this.verticalCenter ? 0 : this.top + "px"
			}
		},
		bodyStyles() {
			return {
				"height": isNaN(this.height) ? this.height : this.height - 54 - 51 + "px",
				"background": this.background
			}
		},
	},
	methods: {
		onInput(v) {
			this.$emit("input", v)
		},
		onClose(e) {
			this.close()
		},
		onCancel() {
			this.$emit("on-cancel")
		},
		onOk() {
			this.$emit("on-ok")
		},
		onVisibleChange(val) {
			this.$emit("on-visible-change", val)
		},
		onHidden() {
			this.$emit("on-hidden")
		},
		onBtnClickHandler(btn) {
			var name = btn.name,
				queue = this.btnClickHook[name] || [],
				options = {}

			if (this.btnHookRunning) return

			this.btnHookRunning = true
			this.loadingBtnName = name

			// 执行按钮响应队列
			runQueue(queue, (hook, next) => {
				hook(next, options)
			}, () => {
				var ops = Object.assign({}, btn, options)

				if (ops.closeModal) {
					// 队列执行完毕后关闭Modal
					this.close(ops)
				} else {
					this.unloadingBtn()
				}
			})
		},
		// 取消按钮加载状态
		unloadingBtn() {
			this.btnHookRunning = false
			this.loadingBtnName = ""
		},
		// 触发Modal关闭
		close(ops) {
			if (this.loadingBtnName) {
				this.$emit(`close-by-${this.loadingBtnName}`, ops)
			}
			this.unloadingBtn()
			this.$refs.innerModal.close()
		},
		// 按Esc键关闭弹窗
		escClose(e) {
			var m = this.$refs.innerModal

			if (m && m.visible && this.closable) {
				if (e.keyCode === 27) {
					this.close()
				}
			}
		},
		maskClick(e) {
			const className = event.target.getAttribute('class');
			if (className && className.indexOf(this.prefixCls + "-wrap") > -1) {
				if (this.maskClosable) {
					this.close()
				}
			}
		},
		getChildren() {
			var $innerModal = this.$refs.innerModal

			return $innerModal.$children
		},
		beforeOpen(fn) {
			this.beforeOpenHook.push(fn)
		},
		beforeEnter(fn) {
			this.beforeEnterHook.push(fn)
		},
		afterEnter(fn) {
			this.afterEnterHook.push(fn)
		},
		beforeBtnClick(name, fn) {
			this.onBtnClick(name, fn)
		},
		// depreciated api，recommand beforeBtnClick
		onBtnClick(name, fn) {
			this.btnClickHook[name] ?
				this.btnClickHook[name].push(fn) :
				this.btnClickHook[name] = [fn]
		},
		resetHook() {
			this.beforeOpenHook = this.modalBeforeOpen ? [this.modalBeforeOpen] : []
			this.beforeEnterHook = this.modalBeforeEnter ? [this.modalBeforeEnter] : []
			this.afterEnterHook = this.modalAfterEnter ? [this.modalAfterEnter] : []
			this.btnClickHook = this.normalizeBtnClickHook(this.modalBeforeBtnClick)
		},
		updateBtns(name, btn) {
			var btns

			// (btns)
			if (type(name) == "array") {
				btns = name

				this.normalBtns = this.normalizeButton(btns)
			}

			// (name, btn)
			if (type(name) == "string" && type(btn) == "object") {
				var target = this.normalBtns.find(v => v.name == name)

				if (target) {
					Object.assign(target, btn)
				}
			}

			// 通知父组件以酌情处理
			this.$emit("update:buttons", this.normalBtns)
		},
		updateTitle(title) {
			this.$emit("update:title", title)
		},

		normalizeBtnClickHook,

		setCountDown(btn, value) {
			btn.countDown = value
		},
		// 打开窗口
		openModal() {
			this.canOpen = true

			// 非异步流程，取消beforeEnterHook
			if (!this.async) {
				return
			}

			var modal = this,
				$loadingBar = this.$refs.loadingBar

			$loadingBar.start()
			this.loading = true

			// 等待子组件初始化
			this.$nextTick(function() {
				runQueue(modal.beforeEnterHook, (hook, next) => {
					hook(next)
				}, () => {
					// 加载完毕
					// console.log("Modal加载完毕")
					modal.loading = false
					$loadingBar.finish()

					runQueue(modal.afterEnterHook, (hook, next) => {
						hook(next)
					})
				})
			})
		},
		// 关闭窗口
		closeModal() {
			this.loading = this.async
			this.canOpen = false
			this.$refs.loadingBar.reset()

			// 防止事件重复注册
			this.resetHook()
		},
		// 规格化btn
		normalizeButton(btns) {
			return btns.map(t => Object.assign({
				disabled: false,
				closeModal: true,
				// count-down
				countDown: -1,
				countStart: () => {},
				countEnd: () => {},
			}, t))
		},
	},
	mounted() {
		if (this.closable) {
			document.addEventListener('keydown', this.escClose)
		}
	},
	watch: {
		value(val) {
			var modal = this

			// before open
			if (val) {
				// 等待子组件创建
				this.$nextTick(function() {
					runQueue(modal.beforeOpenHook, (hook, next) => hook(next), done => {
						// 打开窗口
						if (done) {
							modal.openModal()
						} else {
							// 需要触发input事件
							modal.close()
						}
					})
				})
			} else {
				modal.closeModal()
			}
		},

		buttons: {
			handler(val) {
				if(val !== this.normalBtns){
					this.normalBtns = this.normalizeButton(val)	
				}
			},
			immediate: true
		}
	}
}
</script>